// ─── Contextual Help Content for Bootstrap Wizard Credential Fields ──────────
// All help content lives here — never hardcoded in JSX components.
// Keyed by field ID matching the wizard service step field keys.

export type FieldId =
  | 'github-pat'
  | 'supabase-url'
  | 'supabase-anon-key'
  | 'supabase-service-role-key'
  | 'railway-token'
  | 'netlify-token'
  | 'cloudflare-token'
  | 'cloudflare-zone-id'
  | 'mailgun-api-key'
  | 'stripe-secret-key'
  | 'stripe-webhook-secret'
  | 'openai-api-key';

/**
 * Maps wizard field keys (service:fieldKey) to FieldId for help lookup.
 * Format: "service:fieldKey" → FieldId
 */
export const FIELD_KEY_TO_HELP_ID: Record<string, FieldId> = {
  'github:token': 'github-pat',
  'supabase:url': 'supabase-url',
  'supabase:serviceRoleKey': 'supabase-service-role-key',
  'railway:token': 'railway-token',
  'netlify:token': 'netlify-token',
  'cloudflare:token': 'cloudflare-token',
  'cloudflare:zoneId': 'cloudflare-zone-id',
  'mailgun:apiKey': 'mailgun-api-key',
  'stripe:secretKey': 'stripe-secret-key',
  'openai:apiKey': 'openai-api-key',
};

export interface HelpStep {
  text: string;
}

export interface HelpLink {
  label: string;
  url: string;
}

export interface HelpContent {
  why: string;
  what: string;
  steps: HelpStep[];
  link: HelpLink;
  security: string;
}

export const HELP_CONTENT: Record<FieldId, HelpContent> = {
  'github-pat': {
    why: 'OCNexus creates and manages GitHub repositories on your behalf.',
    what: 'Lets OCNexus create repos, push code, and open pull requests in your GitHub account.',
    steps: [
      { text: 'Go to GitHub \u2192 Settings \u2192 Developer Settings \u2192 Personal Access Tokens \u2192 Tokens (classic)' },
      { text: 'Click "Generate new token (classic)"' },
      { text: 'Set expiration to "No expiration" (you can rotate it later)' },
      { text: 'Select scopes: repo, workflow, admin:org (if using an org account)' },
      { text: 'Click "Generate token" and copy it immediately \u2014 GitHub only shows it once' },
    ],
    link: { label: 'Create GitHub Token', url: 'https://github.com/settings/tokens/new' },
    security: 'Stored encrypted in your Supabase instance. OCNexus never stores it on our servers.',
  },

  'supabase-url': {
    why: 'OCNexus stores your project data, pipeline runs, and user records in your own Supabase database \u2014 not ours.',
    what: 'Connects OCNexus to your Supabase project so all your data stays in your infrastructure.',
    steps: [
      { text: 'Go to your Supabase project dashboard' },
      { text: 'Click "Project Settings" \u2192 "API"' },
      { text: 'Copy the "Project URL"' },
    ],
    link: { label: 'Supabase API Settings', url: 'https://supabase.com/dashboard/project/_/settings/api' },
    security: 'The project URL is not a secret \u2014 it identifies your Supabase instance.',
  },

  'supabase-anon-key': {
    why: 'OCNexus stores your project data, pipeline runs, and user records in your own Supabase database \u2014 not ours.',
    what: 'Connects OCNexus to your Supabase project so all your data stays in your infrastructure.',
    steps: [
      { text: 'Go to your Supabase project dashboard' },
      { text: 'Click "Project Settings" \u2192 "API"' },
      { text: 'Copy the "anon / public" key' },
    ],
    link: { label: 'Supabase API Settings', url: 'https://supabase.com/dashboard/project/_/settings/api' },
    security: 'The anon key is safe to use in client apps. Your service role key is never requested here.',
  },

  'supabase-service-role-key': {
    why: 'Required for server-side operations like creating tables and running migrations.',
    what: 'Lets the OCNexus API perform admin-level database operations during setup only.',
    steps: [
      { text: 'Go to your Supabase project dashboard' },
      { text: 'Click "Project Settings" \u2192 "API"' },
      { text: 'Copy the "service_role" key (listed under "Project API keys")' },
    ],
    link: { label: 'Supabase API Settings', url: 'https://supabase.com/dashboard/project/_/settings/api' },
    security: 'This key bypasses row-level security. It is stored encrypted and used only server-side \u2014 never exposed to the browser.',
  },

  'railway-token': {
    why: 'OCNexus deploys and manages your backend services on Railway.',
    what: 'Lets OCNexus create Railway projects, set environment variables, and trigger deployments.',
    steps: [
      { text: 'Go to railway.app and log in' },
      { text: 'Click your avatar (top right) \u2192 "Account Settings"' },
      { text: 'Click "Tokens" \u2192 "Create Token"' },
      { text: 'Name it "OCNexus" and copy the token' },
    ],
    link: { label: 'Railway Account Tokens', url: 'https://railway.app/account/tokens' },
    security: 'Stored encrypted. OCNexus only accesses Railway projects it creates \u2014 not your existing ones.',
  },

  'stripe-secret-key': {
    why: 'If your project has paid features, OCNexus can wire up Stripe billing for you.',
    what: 'Lets OCNexus create Stripe products, prices, and checkout sessions in your Stripe account.',
    steps: [
      { text: 'Go to your Stripe dashboard' },
      { text: 'Click "Developers" \u2192 "API Keys"' },
      { text: 'Copy the "Secret key" (starts with sk_live_ for production or sk_test_ for testing)' },
      { text: 'Start with the test key \u2014 you can add the live key later' },
    ],
    link: { label: 'Stripe API Keys', url: 'https://dashboard.stripe.com/apikeys' },
    security: 'Never use your live key until you have tested with sk_test_. Stored encrypted, used only server-side.',
  },

  'stripe-webhook-secret': {
    why: 'Lets OCNexus receive real-time payment events from Stripe (subscription started, payment failed, etc.).',
    what: 'Verifies that webhook events actually came from Stripe and not a third party.',
    steps: [
      { text: 'Go to Stripe dashboard \u2192 "Developers" \u2192 "Webhooks"' },
      { text: 'Click "Add endpoint"' },
      { text: 'Set the endpoint URL to your OCNexus API URL + /webhooks/stripe' },
      { text: 'Select events: customer.subscription.updated, customer.subscription.deleted, invoice.payment_succeeded, invoice.payment_failed' },
    ],
    link: { label: 'Stripe Webhooks', url: 'https://dashboard.stripe.com/webhooks' },
    security: 'This is a verification secret, not an access key. Safe to rotate without service interruption.',
  },

  'netlify-token': {
    why: 'OCNexus deploys your frontend (React, Next.js, static sites) to Netlify.',
    what: 'Lets OCNexus create Netlify sites, configure build settings, and trigger deployments.',
    steps: [
      { text: 'Go to app.netlify.com and log in' },
      { text: 'Click your avatar (top right) \u2192 "User settings"' },
      { text: 'Click "Applications" \u2192 "Personal access tokens"' },
      { text: 'Click "New access token", name it "OCNexus", and copy it' },
    ],
    link: { label: 'Netlify Access Tokens', url: 'https://app.netlify.com/user/applications#personal-access-tokens' },
    security: 'Stored encrypted. Used only for deploying sites OCNexus creates.',
  },

  'cloudflare-token': {
    why: 'OCNexus can automatically configure DNS records for your custom domains.',
    what: 'Creates DNS records pointing your domain to Railway, Netlify, and Mailgun services.',
    steps: [
      { text: 'Go to dash.cloudflare.com \u2192 My Profile \u2192 API Tokens' },
      { text: 'Click "Create Token" \u2192 use the "Edit zone DNS" template' },
      { text: 'Under Zone Resources, select your domain' },
      { text: 'Click "Continue to summary" \u2192 "Create Token" and copy it' },
    ],
    link: { label: 'Cloudflare API Tokens', url: 'https://dash.cloudflare.com/profile/api-tokens' },
    security: 'Scoped to DNS editing only. Cannot access your site content or settings.',
  },

  'cloudflare-zone-id': {
    why: 'Identifies which Cloudflare zone (domain) to create DNS records in.',
    what: 'The Zone ID tells OCNexus which domain to configure. Optional \u2014 OCNexus can auto-detect it.',
    steps: [
      { text: 'Go to dash.cloudflare.com and select your domain' },
      { text: 'On the Overview page, scroll down to the right sidebar' },
      { text: 'Copy the "Zone ID" value' },
    ],
    link: { label: 'Cloudflare Dashboard', url: 'https://dash.cloudflare.com' },
    security: 'The Zone ID is not a secret \u2014 it only identifies your domain.',
  },

  'mailgun-api-key': {
    why: 'OCNexus can set up transactional email (password resets, notifications) for your project.',
    what: 'Lets OCNexus configure Mailgun domains, routes, and send emails on behalf of your app.',
    steps: [
      { text: 'Go to app.mailgun.com and log in' },
      { text: 'Click "Settings" \u2192 "API Keys"' },
      { text: 'Copy your "Private API key" (starts with key-)' },
    ],
    link: { label: 'Mailgun API Keys', url: 'https://app.mailgun.com/settings/api_security' },
    security: 'Stored encrypted. Used only for email domain setup and sending.',
  },

  'openai-api-key': {
    why: 'OCNexus uses OpenAI Codex to write the actual code in your pipeline runs.',
    what: 'Authorizes OCNexus to call the OpenAI API for code generation. Billed directly to your OpenAI account.',
    steps: [
      { text: 'Go to platform.openai.com' },
      { text: 'Click your org name (top right) \u2192 "API Keys"' },
      { text: 'Click "Create new secret key"' },
      { text: 'Name it "OCNexus" and copy it immediately' },
    ],
    link: { label: 'OpenAI API Keys', url: 'https://platform.openai.com/api-keys' },
    security: 'Stored encrypted. OCNexus only uses this for pipeline code generation \u2014 not for any other purpose.',
  },
};
